import type { ReactNode } from "react";
import { PageIntro, PageLabel } from "~/components/inside/page-intro";
import { PageScene } from "~/components/inside/page-scene";
import { ReadLayout } from "~/components/inside/read-layout";
import { SideIndex } from "~/components/inside/side-index";
import * as m from "~/paraglide/messages.js";

export interface LegalSectionRef {
  /** A stable, language-independent slug: these are deep-linked from elsewhere. */
  id: string;
  title: string;
}

interface LegalPageProps {
  children: ReactNode;
  label: string;
  lede: string;
  /** Drives the side index. Each entry must match a `LegalSection` below. */
  sections: LegalSectionRef[];
  title: string;
}

/**
 * The shell the three legal pages share.
 *
 * Visitor mode here is Read: the opening band names the document and when it
 * was last changed, and everything after it is one column with the sections
 * beside it. The two hand-rolled jump-navs privacy and terms each carried are
 * now the same side index the articles use.
 */
export function LegalPage({
  children,
  label,
  lede,
  sections,
  title,
}: Readonly<LegalPageProps>) {
  return (
    <>
      <PageScene variant="heading">
        <div>
          <PageLabel>{label}</PageLabel>
          <h1>{title}</h1>
          <PageIntro>{lede}</PageIntro>
        </div>
      </PageScene>

      <ReadLayout
        index={
          sections.length > 0 ? (
            <SideIndex
              items={sections.map((section) => ({
                href: `#${section.id}`,
                label: section.title,
              }))}
              title={m.legal_index_title()}
            />
          ) : null
        }
      >
        <div className="legal-body">{children}</div>
      </ReadLayout>
    </>
  );
}

/** A deep-linkable section of a legal document. */
export function LegalSection({
  children,
  id,
  title,
}: Readonly<{ children: ReactNode; id: string; title: string }>) {
  return (
    <section className="legal-section" id={id}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

/**
 * A term and its value in the requisites list, with an optional note under it.
 *
 * Not the shared `Facts`: these values carry links, codes and a sentence of
 * statutory explanation, where `Facts` takes plain strings.
 */
export function Requisite({
  children,
  note,
  term,
}: Readonly<{ children: ReactNode; note?: string; term: string }>) {
  return (
    <div>
      <dt>{term}</dt>
      <dd>
        <span>{children}</span>
        {note ? <span className="facts-note">{note}</span> : null}
      </dd>
    </div>
  );
}
